import React from 'react';
import './Styles/VideoCard.css';
import axiosClient from '../services/axios_client';
import videoPlaceholder from '../assets/video_place_holder.png';

type Category = { id: string | number; name: string; };
type Language = { id: string | number; name: string; };

// Updated Video type to include all necessary data
type Video = {
  id: string | number;
  title: string;
  description: string | null;
  category: Category | null;
  language: Language | null;
  thumbnail_path: string | null;
  comments: number;
  likes: number;
};

type Props = {
  video: Video;
  onClick: (video: Video) => void;
  lastRef?: (node: Element | null) => void;
};

// Helper function to trim text
const trimText = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, text.lastIndexOf(' ', maxLength)) + '…';
};

const VideoCard: React.FC<Props> = ({ video, onClick, lastRef }) => {
  const baseUrl = process.env.REACT_APP_EDUCATION_VIDEO_BASE_URL;
  const thumbnailUrl = video.thumbnail_path
    ? `${baseUrl}/storage/${video.thumbnail_path}`
    : videoPlaceholder;

  const trimmedDescription = trimText(video.description, 80);

  return (
    <article
      ref={lastRef}
      className="video-card-redesigned"
      role="button"
      tabIndex={0}
      onClick={() => onClick(video)}
      onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && onClick(video)}
    >
      <div className="thumb-wrapper">
        <div className="thumb" style={{ backgroundImage: `url(${thumbnailUrl})` }} />
        <div className="thumb-overlay">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polygon points="10 8 16 12 10 16 10 8"></polygon></svg>
        </div>
        <div className="pills">
          {video.category && <span className="pill category">{video.category.name}</span>}
          {video.language && <span className="pill language">{video.language.name}</span>}
        </div>
      </div>
      <div className="video-body">
        <h3 className="title" title={video.title}>{video.title}</h3>
        {trimmedDescription && <p className="description">{trimmedDescription}</p>}
        <div className="stats">
          <div className="stat-item">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg>
            <span>{video.likes}</span>
          </div>
          <div className="stat-item">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
            <span>{video.comments}</span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default React.memo(VideoCard);